import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { AdminSendOutcome } from "../api/types";
import { ErrorNotice, Loading } from "../components/Notice";
import { PageHead } from "../components/PageHead";
import { ReadChip } from "../components/StatusChip";
import { formatDateTime, orDash } from "../format";
import { useCompact } from "../hooks";
import { SECTIONS } from "../sections";
import { useSession } from "../session/SessionContext";

/**
 * The audit trail: what was sent, to whom, and whether the recipient has opened
 * it. "Sent" and "read" are the only two facts the API has. There is no
 * delivered state anywhere in this screen because there is none in the data.
 */
export function Notifications() {
  const { api, staff } = useSession();
  const compact = useCompact();
  const [items, setItems] = useState<AdminSendOutcome[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Same invalidation counter as Referrals: the first-page effect bumps it on
  // the way out, so a `loadMore` that lands after the list has been reloaded
  // cannot append rows or install a cursor from the superseded request.
  const generation = useRef(0);

  useEffect(() => {
    const current = generation.current;
    api
      .outcomes({})
      .then((page) => {
        if (current !== generation.current) return;
        setItems(page.items);
        setNextCursor(page.nextCursor);
      })
      .catch((err: unknown) => {
        if (current === generation.current) setError(err);
      });
    return () => {
      generation.current += 1;
    };
  }, [api]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    const current = generation.current;
    setLoadingMore(true);
    try {
      const page = await api.outcomes({ cursor: nextCursor });
      if (current !== generation.current) return;
      setItems((prev) => [...(prev ?? []), ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      if (current === generation.current) setError(err);
    } finally {
      setLoadingMore(false);
    }
  }, [api, nextCursor, loadingMore]);

  return (
    <div className="page">
      <PageHead title={SECTIONS.notifications.label} icon={<SECTIONS.notifications.Icon />}>
        <p className="subtle">
          {staff?.role === "admin" ? "Every send authored by staff." : "Everything you have sent."}
        </p>
      </PageHead>

      <ErrorNotice error={error} />

      <div className="panel">
        {items === null && !error ? (
          <Loading what="notifications" />
        ) : items && items.length === 0 ? (
          <div className="empty">
            <strong>No notifications sent</strong>
            <p className="subtle">
              Open a user from <Link to="/">Referrals</Link> to send one.
            </p>
          </div>
        ) : compact ? (
          <ul className="list">
            {(items ?? []).map((item) => (
              <li key={item.id} className="list-row">
                <span className="list-main">
                  <span className="list-title">
                    <span className="list-title-text">{item.title}</span>
                  </span>
                  <span className="list-body">{item.body}</span>
                  <span className="list-meta">
                    <span>{orDash(item.recipient.email)}</span>
                    <span>{orDash(item.screen)}</span>
                    <span>{formatDateTime(item.sentAt)}</span>
                  </span>
                </span>
                <span className="list-trail">
                  <ReadChip item={item} short />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Title</th>
                  <th>Destination</th>
                  <th className="num">Sent</th>
                  <th>Read</th>
                </tr>
              </thead>
              <tbody>
                {(items ?? []).map((item) => (
                  <tr key={item.id}>
                    <td className="cell-strong">{orDash(item.recipient.email)}</td>
                    <td>
                      <div className="cell-strong">{item.title}</div>
                      <div className="subtle">{item.body}</div>
                    </td>
                    <td>{orDash(item.screen)}</td>
                    <td className="num">{formatDateTime(item.sentAt)}</td>
                    <td>
                      <ReadChip item={item} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {nextCursor ? (
          <div className="panel-foot">
            <button type="button" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? "Loading..." : "Load more"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
